import * as React from 'react';

import {ContentBlock, ContentState} from 'draft-js';
import linkify from 'src/utils/linkify';

import WarningMessage from 'src/components/lib/messaging-input/warning-message';
import UrlHighlightMessage from 'src/components/lib/messaging-input/url-highlight-message';


export const PUNCTUATION_VARIABLE_REGEX: RegExp =
  /([!#$%&'()*+,-.:;?@[\\\]^_`{|}~]{2,})/g;
export const EMOJI_VARIABLE_REGEX: RegExp = new RegExp(
  ['💰', '🤑', '💸', '💳', '💵', '💲'].join('|'),
  'g',
);
export const CAPS_VARIABLE_REGEX: RegExp = /([A-Z]{3,})/g;
export const CAPS_VARIABLE_REGEX_RESOLVE: RegExp =
  /([A-Z]{3,} .+[A-Z]{3,} .+[A-Z]{3,})/;
export const URL_SHORTENER_VARIABLE_REGEX: RegExp =
  /((http:\/\/|https:\/\/)?(bit.ly|ow.ly|tinyurl|rb.gy))[^ ]+/g;
export const EMAIL_REGEX: RegExp =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const ENTITY_PUNCTUATION = 'PUNCTUATION';
export const ENTITY_EMOJI = 'EMOJI';
export const ENTITY_CAPS = 'CAPS';
export const ENTITY_URL_SHORTENER = 'URL_SHORTENER';

// code for finding entities for different types

const findPunctuationEntities = (
  contentBlock: ContentBlock,
  callback: any,
  contentState: ContentState,
) => {
  findPunctuationVariables(contentBlock.getText(), callback);
};

export const findPunctuationVariables = (
  text: string,
  callback: (start: number, stop: number) => number,
) => {
  let match;
  while ((match = PUNCTUATION_VARIABLE_REGEX.exec(text)) !== null) {
    if (match) {
      const start = match.index;
      callback(start, start + match[0].length);
    }
  }
};

function findEmojiEntities(
  contentBlock: ContentBlock,
  callback: any,
  contentState: ContentState,
) {
  findEmojiVariables(contentBlock.getText(), callback);
}

export const findEmojiVariables = (
  text: string,
  callback: (start: number, stop: number) => number,
) => {
  let match;
  while ((match = EMOJI_VARIABLE_REGEX.exec(text)) !== null) {
    if (match) {
      const start = match.index;
      callback(start, start + match[0].length);
    }
  }
};

function findCapsEntities(
  contentBlock: ContentBlock,
  callback: any,
  contentState: ContentState,
) {
  const text = contentBlock.getText();
  if (CAPS_VARIABLE_REGEX_RESOLVE.exec(text) !== null) {
    findCapsVariables(text, callback);
  }
}

export const findCapsVariables = (
  text: string,
  callback: (start: number, stop: number) => number,
) => {
  let match;
  while ((match = CAPS_VARIABLE_REGEX.exec(text)) !== null) {
    if (match) {
      const start = match.index;
      callback(start, start + match[0].length);
    }
  }
};

function findUrlShortenerEntities(
  contentBlock: ContentBlock,
  callback: any,
  contentState: ContentState,
) {
  findUrlShortenerVariables(contentBlock.getText(), callback);
}

// Recognizing the pattern in the text and replacing them with the text

export const findUrlShortenerVariables = (
  text: string,
  callback: (start: number, stop: number) => number,
) => {
  let match;
  while ((match = URL_SHORTENER_VARIABLE_REGEX.exec(text)) !== null) {
    if (match) {
      const start = match.index;
      callback(start, start + match[0].length);
    }
  }
};

// component which applies the styling required to the text

const ContentWarningVariable =
  (type: string) =>
  // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type. | TS7031 - Binding element 'start' implicitly has an 'any' type. | TS7031 - Binding element 'end' implicitly has an 'any' type.
  ({children, start, end}) =>
    (
      <WarningMessage type={type} start={start} end={end}>
        {children}
      </WarningMessage>
    );

// Initial places where the code is when the text is typed in the input box

export const PunctuationDecorator = () => ({
  component: ContentWarningVariable('punctuation'),
  strategy: findPunctuationEntities,
});

export const EmojiDecorator = () => ({
  component: ContentWarningVariable('emoji'),
  strategy: findEmojiEntities,
});

export const CapsDecorator = () => ({
  component: ContentWarningVariable('caps'),
  strategy: findCapsEntities,
});

export const UrlShortenerDecorator = () => ({
  component: ContentWarningVariable('url_shortener'),
  strategy: findUrlShortenerEntities,
});

const URLHighlightSpan = (props: any) => {
  return <UrlHighlightMessage>{props.children}</UrlHighlightMessage>;
};

const findUrlsToHighlight = (
  contentBlock: any,
  callback: any,
  contentState: any,
) => {
  const text = contentBlock.getText();
  const matches = linkify().match(text);
  if (matches) {
    // @ts-expect-error - TS7006 - Parameter 'match' implicitly has an 'any' type.
    matches.forEach((match) => {
      let expandedStart = match.index;
      //note: (Ashwini) Adding \n as escape character, we may also need other escape characters
      while (expandedStart > 0 && text[expandedStart - 1] === '\n') {
        expandedStart--;
      }
      // Only highlight URLs longer than 10 characters and non-emails
      if (match.raw.length > 10 && !EMAIL_REGEX.test(match.raw)) {
        callback(expandedStart, match.lastIndex);
      }
    });
  }
};

export const UrlHighlightDecorator = () => ({
  component: URLHighlightSpan,
  strategy: findUrlsToHighlight,
});
